import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Grid, Card, CardContent, Chip, LinearProgress, Divider, Switch, FormControlLabel} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton } from '@mui/material';
import { projectService } from '../services/api';
import Layout from '../components/Layout';
import FileUpload from '../components/FileUpload';
import api from '../services/api';

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({ contracts: 0, invoices: 0, pos: 0, budget_used: 0 });
  const [members, setMembers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [shareDialog, setShareDialog] = useState({ open: false, doc: null });
  const [shareForm, setShareForm] = useState({ gc: false, trades: [], is_shared: false });
  const [shareTradeOptions, setShareTradeOptions] = useState([]);
  const [docDialog, setDocDialog] = useState(false);
  const [docForm, setDocForm] = useState({ name: '', category: 'general', description: '' });
  const [docFiles, setDocFiles] = useState([]);
  const [docSaving, setDocSaving] = useState(false);
  const [gcUsers, setGcUsers] = useState([]);
  const [editImages, setEditImages] = useState([]);

  useEffect(() => {
    projectService.getById(id).then(res => setProject(res.data)).catch(() => navigate('/projects'));
    api.get(`/projects/${id}/members`).then(r => setMembers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get(`/documents?project_id=${id}`).then(r => setDocuments(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/settings/trades').then(r => setShareTradeOptions((r.data||[]).filter(t=>t.is_active!==false).map(t=>t.name))).catch(()=>{});
    api.get('/auth/users/by-role?roles=general_contractor').then(r => setGcUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    // Fetch related stats
    Promise.all([
      api.get('/contracts').catch(() => ({ data: [] })),
      api.get('/purchase-orders').catch(() => ({ data: [] })),
      api.get('/invoices').catch(() => ({ data: [] })),
    ]).then(([contracts, pos, invoices]) => {
      const pc = contracts.data.filter(c => c.project_id === id);
      const pp = pos.data.filter(p => p.project_id === id);
      const pi = invoices.data.filter(i => i.project_id === id);
      setStats({
        contracts: pc.length,
        pos: pp.length,
        invoices: pi.length,
        budget_used: pp.reduce((s, p) => s + Number(p.invoiced_amount || 0), 0),
      });
    });
  }, [id]);

  if (!project) return null;

  const budgetUsedPct = project.total_budget > 0
    ? Math.min(100, (stats.budget_used / project.total_budget) * 100) : 0;

  const statusColor = { active: { bg: '#e8f5e9', text: '#2e7d32' }, completed: { bg: '#e3f2fd', text: '#1565c0' }, on_hold: { bg: '#fff3e0', text: '#e65100' }, cancelled: { bg: '#ffebee', text: '#c62828' } };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Back button */}
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')}
          sx={{ mb: 3, color: '#666', fontWeight: 600 }}>
          Back to Projects
        </Button>

        {/* Hero image */}
        <Box sx={{ borderRadius: 3, overflow: 'hidden', mb: 3, height: 280, position: 'relative',
          background: project.image_url ? 'none' : 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
          display: 'flex', alignItems: 'flex-end' }}>
          {project.image_url && (
            <img src={project.image_url} alt={project.name}
              style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
          <Box sx={{ position: 'relative', width: '100%', p: 3,
            background: 'linear-gradient(0deg, rgba(0,0,0,0.7) 0%, transparent 100%)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <Box>
                <Chip label={project.status} size="small"
                  sx={{ mb: 1, bgcolor: statusColor[project.status]?.bg, color: statusColor[project.status]?.text, fontWeight: 700 }} />
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 800 }}>{project.name}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>{project.address}, {project.city}, {project.state}</Typography>
              </Box>
              <Button variant="outlined" startIcon={<EditIcon />}
                sx={{ color: 'white', borderColor: 'white', borderRadius: 2 }}
                onClick={() => { setEditForm({
                  name: project.name||'', description: project.description||'',
                  address: project.address||'', city: project.city||'',
                  state: project.state||'', zip_code: project.zip_code||'',
                  total_budget: project.total_budget||'', status: project.status||'active',
                  gc_id: project.gc_id||'',
                  image_url: project.image_url||'',
                  start_date: project.start_date ? project.start_date.split('T')[0] : '',
                  end_date: project.end_date ? project.end_date.split('T')[0] : '',
                }); setEditDialog(true); }}>
                EDIT
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Stats row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Budget', value: `$${Number(project.total_budget).toLocaleString()}`, color: '#1a237e' },
            { label: 'Contracts', value: stats.contracts, color: '#2e7d32' },
            { label: 'Purchase Orders', value: stats.pos, color: '#e65100' },
            { label: 'Invoices', value: stats.invoices, color: '#6a1b9a' },
          ].map(stat => (
            <Grid item xs={6} md={3} key={stat.label}>
              <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 2, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: stat.color }}>{stat.value}</Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>{stat.label}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Budget progress */}
        <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 3, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a237e', mb: 2 }}>Budget Overview</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#666' }}>Invoiced so far</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              ${stats.budget_used.toLocaleString()} / ${Number(project.total_budget).toLocaleString()}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={budgetUsedPct}
            sx={{ height: 10, borderRadius: 5, bgcolor: '#e8eaf6',
              '& .MuiLinearProgress-bar': { bgcolor: budgetUsedPct > 80 ? '#c62828' : '#1a237e', borderRadius: 5 } }} />
          <Typography variant="caption" sx={{ color: '#666', mt: 0.5, display: 'block' }}>
            {budgetUsedPct.toFixed(1)}% of budget used
          </Typography>
        </Card>

        {/* Project Info */}
        <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 3, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a237e', mb: 2 }}>Project Details</Typography>
          <Grid container spacing={2}>
            {project.description && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>DESCRIPTION</Typography>
                <Typography variant="body2" sx={{ color: '#444', mt: 0.5 }}>{project.description}</Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>ADDRESS</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{project.address}, {project.city}, {project.state} {project.zip_code}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>START DATE</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{project.start_date ? new Date(project.start_date).toLocaleDateString() : '—'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>END DATE</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{project.end_date ? new Date(project.end_date).toLocaleDateString() : '—'}</Typography>
            </Grid>
            {project.gc_id && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>GENERAL CONTRACTOR</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: '#1a237e', fontWeight: 600 }}>{project.gc_name || project.gc_email || project.gc_id}</Typography>
              </Grid>
            )}
            {members.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>PROJECT MEMBERS</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                  {members.map(m => (
                    <Chip key={m.id} size="small"
                      label={`${m.first_name} ${m.last_name} (${m.user_role?.replace(/_/g,' ')})`}
                      sx={{ bgcolor: '#e8eaf6', color: '#1a237e', fontWeight: 600 }} />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </Card>

        {/* Quick links */}
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a237e', mb: 2 }}>Quick Actions</Typography>
        <Grid container spacing={2}>
          {[
            { label: 'View Contracts', path: `/contracts?project_id=${id}`, color: '#1a237e' },
            { label: 'View Purchase Orders', path: `/purchase-orders?project_id=${id}`, color: '#e65100' },
            { label: 'View Budget', path: `/budget?project_id=${id}`, color: '#2e7d32' },
            { label: 'View Invoices', path: `/invoices?project_id=${id}`, color: '#6a1b9a' },
          ].map(action => (
            <Grid item xs={6} md={3} key={action.label}>
              <Button fullWidth variant="outlined" onClick={() => navigate(action.path)}
                sx={{ py: 1.5, borderRadius: 2, borderColor: action.color, color: action.color, fontWeight: 600 }}>
                {action.label}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Documents Section */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>Project Documents</Typography>
          <Button variant="contained" size="small" startIcon={<span>+</span>}
            onClick={() => setDocDialog(true)}
            sx={{ bgcolor: '#1a237e', borderRadius: 2 }}>Upload Document</Button>
        </Box>
        {documents.length === 0 ? (
          <Card elevation={0} sx={{ border: '1px dashed #c5cae9', borderRadius: 3, p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No documents uploaded yet.</Typography>
            <Typography variant="caption" color="text.secondary">Upload plans, permits, contracts and other project files.</Typography>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {documents.map(doc => {
              const ext = doc.name.split('.').pop().toLowerCase();
              const icon = ['pdf'].includes(ext) ? '📄' : ['jpg','jpeg','png'].includes(ext) ? '🖼️' : ['doc','docx'].includes(ext) ? '📝' : '📁';
              const catColor = { plans: '#1a237e', permit: '#e65100', contract: '#2e7d32', general: '#555', other: '#7b1fa2' };
              return (
                <Grid item xs={12} sm={6} md={4} key={doc.id}>
                  <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, p: 2,
                    '&:hover': { boxShadow: '0 2px 12px rgba(26,35,126,0.1)' } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', gap: 1.5, flex: 1 }}>
                        <Typography sx={{ fontSize: 28 }}>{icon}</Typography>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>{doc.name}</Typography>
                          {doc.description && <Typography variant="caption" color="text.secondary" display="block" noWrap>{doc.description}</Typography>}
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                            <Chip label={doc.category} size="small"
                              sx={{ fontSize: 10, height: 18, bgcolor: '#f5f5f5', color: catColor[doc.category]||'#555' }}/>
                            {doc.is_shared && <Chip label="Shared" size="small" sx={{ fontSize: 10, height: 18, bgcolor: '#e8f5e9', color: '#2e7d32' }}/>}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            by {doc.uploaded_by_name} · {new Date(doc.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => window.open(doc.file_url, '_blank')} sx={{ color: '#1a237e' }}>
                          <span style={{ fontSize: 14 }}>👁</span>
                        </IconButton>
                        <IconButton size="small" onClick={() => {
                          const sw = doc.shared_with || {};
                          setShareForm({
                            gc: !!sw.gc,
                            trades: Array.isArray(sw.trades) ? sw.trades : [],
                            is_shared: !!doc.is_shared
                          });
                          setShareDialog({ open: true, doc });
                        }} sx={{ color: doc.is_shared ? '#2e7d32' : '#999' }}>
                          <span style={{ fontSize: 14 }}>🔗</span>
                        </IconButton>
                        <IconButton size="small" onClick={() => {
                          api.delete(`/documents/${doc.id}`);
                          setDocuments(docs => docs.filter(d => d.id !== doc.id));
                        }} sx={{ color: '#e53935' }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>


      {/* Share Document Dialog */}
      <Dialog open={shareDialog.open} onClose={() => setShareDialog({ open: false, doc: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', borderBottom: '1px solid #e0e0e0' }}>Share "{shareDialog.doc?.name}"</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControlLabel
              control={<Switch checked={shareForm.is_shared} onChange={e => setShareForm({ ...shareForm, is_shared: e.target.checked })} />}
              label="Document is shared (visible to selected roles/trades below)"
            />
            <FormControlLabel
              control={<Switch checked={shareForm.gc} onChange={e => setShareForm({ ...shareForm, gc: e.target.checked })} disabled={!shareForm.is_shared} />}
              label="Visible to General Contractor"
            />
            <Box>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>Visible to subcontractors of these trades:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                {(shareTradeOptions.length ? shareTradeOptions : ['General','Framing','Foundation','Electrical','Plumbing','HVAC','Roofing','Drywall','Painting','Flooring','Landscaping','Concrete','Masonry','Insulation','Tile','Cabinets','Windows & Doors','Other']).map(t => {
                  const sel = shareForm.trades.includes(t);
                  return (
                    <Chip key={t} label={t} size="small" clickable
                      onClick={() => {
                        if (!shareForm.is_shared) return;
                        const next = sel ? shareForm.trades.filter(x => x !== t) : [...shareForm.trades, t];
                        setShareForm({ ...shareForm, trades: next });
                      }}
                      sx={{ bgcolor: sel ? '#1a237e' : '#f5f5f5', color: sel ? 'white' : '#555', fontWeight: sel ? 700 : 400, opacity: shareForm.is_shared ? 1 : 0.5 }} />
                  );
                })}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setShareDialog({ open: false, doc: null })}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            const body = {
              is_shared: shareForm.is_shared,
              shared_with: { gc: shareForm.gc, trades: shareForm.trades, users: (shareDialog.doc?.shared_with?.users || []) }
            };
            try {
              const r = await api.put(`/documents/${shareDialog.doc.id}/share`, body);
              setDocuments(docs => docs.map(d => d.id === shareDialog.doc.id ? r.data : d));
              setShareDialog({ open: false, doc: null });
            } catch (e) { console.error(e); }
          }} sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={docDialog} onClose={() => setDocDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', borderBottom: '1px solid #e0e0e0' }}>Upload Document</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Document Name *" value={docForm.name} onChange={e => setDocForm({...docForm, name: e.target.value})} fullWidth />
            <TextField select label="Category" value={docForm.category} onChange={e => setDocForm({...docForm, category: e.target.value})} fullWidth>
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="plans">Plans / Drawings</MenuItem>
              <MenuItem value="permit">Permits</MenuItem>
              <MenuItem value="contract">Contracts</MenuItem>
              <MenuItem value="inspection">Inspection Reports</MenuItem>
              <MenuItem value="insurance">Insurance</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
            <TextField label="Description" value={docForm.description} onChange={e => setDocForm({...docForm, description: e.target.value})} fullWidth multiline rows={2} />
            <FileUpload onFilesChange={setDocFiles} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setDocDialog(false)}>Cancel</Button>
          <Button variant="contained" disabled={docSaving || !docForm.name || docFiles.length === 0}
            onClick={async () => {
              setDocSaving(true);
              try {
                const file = docFiles[0];
                const r = await api.post('/documents', {
                  project_id: id,
                  name: docForm.name,
                  file_url: file.url || file.gcsUrl,
                  file_type: file.type || file.name?.split('.').pop(),
                  file_size: file.size,
                  category: docForm.category,
                  description: docForm.description,
                });
                setDocuments(docs => [r.data, ...docs]);
                setDocDialog(false);
                setDocForm({ name: '', category: 'general', description: '' });
                setDocFiles([]);
              } catch(e) { console.error(e); }
              setDocSaving(false);
            }}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
            {docSaving ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', borderBottom: '1px solid #e0e0e0' }}>Edit Project</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="Project Name" value={editForm.name||''} onChange={e => setEditForm({...editForm, name: e.target.value})} margin="normal" />
          <TextField fullWidth label="Description" value={editForm.description||''} onChange={e => setEditForm({...editForm, description: e.target.value})} margin="normal" multiline rows={2} />
          <TextField fullWidth label="Address" value={editForm.address||''} onChange={e => setEditForm({...editForm, address: e.target.value})} margin="normal" />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="City" value={editForm.city||''} onChange={e => setEditForm({...editForm, city: e.target.value})} margin="normal" />
            <TextField label="State" value={editForm.state||''} onChange={e => setEditForm({...editForm, state: e.target.value})} margin="normal" sx={{ width: 100 }} />
            <TextField label="ZIP" value={editForm.zip_code||''} onChange={e => setEditForm({...editForm, zip_code: e.target.value})} margin="normal" sx={{ width: 120 }} />
          </Box>
          <TextField fullWidth label="Total Budget ($)" type="number" value={editForm.total_budget||''} onChange={e => setEditForm({...editForm, total_budget: e.target.value})} margin="normal" />
          <TextField select fullWidth label="Status" value={editForm.status||'active'} onChange={e => setEditForm({...editForm, status: e.target.value})} margin="normal">
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="planning">Planning</MenuItem>
            <MenuItem value="on_hold">On Hold</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', mb: 1 }}>Project Photo</Typography>
            {editForm.image_url && (
              <Box sx={{ mb: 1, borderRadius: 2, overflow: 'hidden', height: 120 }}>
                <img src={editForm.image_url} alt="current" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
            )}
            <FileUpload onFilesChange={setEditImages} accept="image/*" />
          </Box>
          <TextField select fullWidth label="General Contractor" value={editForm.gc_id||''} onChange={e => setEditForm({...editForm, gc_id: e.target.value})} margin="normal">
            <MenuItem value="">— No GC assigned —</MenuItem>
            {gcUsers.map(u => <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Start Date" type="date" value={editForm.start_date||''} onChange={e => setEditForm({...editForm, start_date: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
            <TextField fullWidth label="End Date" type="date" value={editForm.end_date||''} onChange={e => setEditForm({...editForm, end_date: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={async () => {
            setSaving(true);
            try {
              const api = require('../services/api').default;
              const imageUrl = editImages.length > 0 ? (editImages[0].url || editImages[0].gcsUrl || editForm.image_url) : editForm.image_url;
              await api.put('/projects/' + project.id, { ...editForm, image_url: imageUrl });
              setProject({...project, ...editForm, image_url: imageUrl});
              setEditImages([]);
              setEditDialog(false);
            } catch(e) { console.error(e); }
            setSaving(false);
          }} sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
